import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {getDate, getHourMinutes} from '../../library/dates';
import {TICK_DOMAIN, TICLI} from '../../config/config';
import {useEffect} from 'react';
import {getEventGroups} from '../../redux/tickets/ticketsActions';
import {useParams} from 'react-router-dom';

export const variableTexts = {
    givechak: {
        eventLink: 'givechakLink',
        eventTitle: 'givechakTitle'
    }
};

const getTimeVariables = (event, branding, t) => {
    const {currentLang, data} = branding;
    return {
        timeOpenstartDate: event?.timeStart ? getDate(event.timeStart) : '',
        timeOpenstartDateHeb: data?.[currentLang]?.date?.hebrewStartDate || data?.[currentLang]?.date?.hebrewDate || '',
        ...(event?.timeOpen && {timeOpenstartTime: getHourMinutes(event.timeOpen)}),
        timeStart: event?.timeStart ? getHourMinutes(event.timeStart) : '',
        timeOpenstartText: `${t('timeOpenstartText')}: `
    };
};

const getGivechakVariables = (event, t, type) => {
    if(event?.type !== 'givechak') return {};
    return {
        refName: t('refName'),
        refTotal: t('refTotal'),
        refTarget: t('refTarget'),
        refBalance: t('refBalance'),
        refLink: `https://${TICLI}/ref=r1`,
        ...(type !== 'lead' && {sendWhatsapp: t('sendWhatsappValue')}),
        ...(type !== 'lead' && {donation: t('donation')}),
    };
};

const getSuspendedVariables = (t) => ({
    billingLineSuspended: t('billingLineSuspendedText')
});

const getTicketVariables = (tickets, t) => {
    const ticketTitle = tickets?.tickets?.length > 0 ? tickets.tickets[0]?.title : '';
    return {
        ticketLink: `https://${TICLI}/ticket`,
        ticketLinkPrint: `https://${TICLI}/ticket?print`,
        ticketName: ticketTitle,
        ...(ticketTitle && {ticketNameAmount: `3 ${ticketTitle}`}),
        extraName: t('extraName'),
        extraNameAmount: `2 ${t('extraName')}`,
        externalVoucher: `${t('externalVoucherValue')} <123456>`,
        ticketText: tickets?.tickets?.[0]?.text || t('ticketTextValue'),
        ...(tickets?.groups?.length > 0 && {groupName: tickets.groups[0]?.title})
    };
};

const getBaseVariables = (event, user, producer, t) => ({
    eventTitle: event?.title || '',
    nameFirst: user?.nameFirst || '',
    nameLast: user?.nameLast || '',
    eventLink: `${TICK_DOMAIN}/${event?.name}?ref=3g7g`,
    invoiceLink: `${t('invoiceLinkValue')} https://${TICLI}/invoice`,
    currencySymbol: event?.currencySymbol || '',
    eventCurrency: event?.currencySymbol || '',
    billingLine: `${t('billingLineText')} ${event?.supplierName}`,
    producerName: producer?.contactName || '',
    producerPhone: producer?.contactPhone || '',
    portalLink: t('portalLink')
});

const getLocationVariables = (event) => ({
    ...(event?.location && {location: event.location}),
    ...(event?.locationMap && {waze: event.waze})
});

const getAreaVariables = (event, t) => ({
    ...(event?.type === 'area' && {seats: t('yourSeats')})
});

const getInvoiceVariables = (invoicePostFields, t) => {
    if(!invoicePostFields) return {};
    return {
        invoiceSum: invoicePostFields.sum || t('invoiceSum'),
        invoicePayment: invoicePostFields.payment || t('invoicePayment'),
        invoiceBillingLink: invoicePostFields.invoice || t('invoiceLink'),
        currencySymbol: invoicePostFields.currency || t('currencySymbol'),
        invoiceCustomerName: invoicePostFields.name || t('invoiceCustomerName'),
        invoiceMonth: invoicePostFields.month || t('invoiceMonth'),
    };
};

export function useVariables(type = 'ticket') {
    const event = useSelector(s => s.event);
    const user = useSelector(s => s.user);
    const tickets = useSelector(s => s.tickets);
    const producer = useSelector(s => s.account.packagesProducer);
    const branding = useSelector(s => s.branding ?? {});
    const account = useSelector(s => s.account);
    const invoicePostFields = useSelector(s => s.cash?.invoicePostFields);
    const {t} = useTranslation('variables');
    const {messageType} = useParams();

    const susStatus = event?.settings?.suspended?.susStatus;
    const suspendedStatus = account?.permissions?.find(item => item.permission === 'suspended')?.status;
    const isSuspended = susStatus && suspendedStatus === 1;

    const timeVariables = getTimeVariables(event, branding, t);
    const givechakVariables = getGivechakVariables(event, t, type);
    const suspendedVariables = getSuspendedVariables(t);
    const baseVariables = getBaseVariables(event, user, producer, t);
    const locationVariables = getLocationVariables(event);
    const areaVariables = getAreaVariables(event, t);
    const ticketVariables = event?.type !== 'givechak' ? getTicketVariables(tickets, t) : {};
    const invoiceVariables = getInvoiceVariables(invoicePostFields, t);

    const leadVariables = {
        eventTitle: event?.title || '',
        ...givechakVariables
    };

    const invoiceVariablesList = {
        eventTitle: event?.title || '',
        producerName: producer?.contactName || '',
        producerPhone: producer?.contactPhone || '',
        portalLink: t('portalLink'),
        ...invoiceVariables
    };

    const defaultVariables = type === 'lead' ? leadVariables : type === 'invoice' ? invoiceVariablesList : {
        ...baseVariables,
        ...locationVariables,
        ...(event?.timeStart && timeVariables),
        ...areaVariables,
        ...(isSuspended && suspendedVariables),
        ...givechakVariables,
        ...ticketVariables,
        ...(messageType === 'desperate' && {advancedLink: t('advancedLink')})
    };

    const disabledVariables = type === 'lead' || type === 'invoice' ? {} : {
        ...(!event?.timeStart && timeVariables),
        ...(!event?.location && {location: event?.location}),
        ...(!event?.locationMap && {waze: event?.waze}),
        ...((susStatus && !isSuspended) && suspendedVariables),
        ...(event?.type !== 'givechak' && !tickets?.tickets?.[0]?.title && {ticketNameAmount: ''})
    };

    useEffect(() => {
        if(!tickets?.getEventGroupsXHR) {
            getEventGroups().then();
        }
    });

    return {defaultVariables, disabledVariables, invoiceVariables};
}
